import { yupResolver } from '@hookform/resolvers/yup'
import AgreementFile from 'assets/files/agreement-conditions.pdf'
import IPServiceAgreement from 'assets/files/ip-service-agreement.pdf'
import TermsOfQuarantee from 'assets/files/terms-of-guarantee.pdf'
import ChevronUpIcon from 'assets/icons/chevron-compact-down.svg?component'
import InfoIcon from 'assets/icons/landing/info.svg?component'
import InfoBlock from 'components/UI/Blocks/InfoBlock'
import Button from 'components/UI/Buttons/Button'
import { Checkbox } from 'components/UI/Checkbox'
import ContainerWithSteps from 'components/UI/Container/ContainerWithSteps'
import MaskedInput from 'components/UI/Inputs/MaskedInput'
import BodyText from 'components/UI/Landing/body-text'
import Link from 'components/UI/Link'
import Select from 'components/UI/Select'
import Typography from 'components/UI/Typography'
import { useAppDispatch, useAppSelector } from 'hooks'
import { Fragment, MouseEvent, useCallback, useEffect, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useGetDecryptedUserInfoMutation } from 'redux/api/authApi'
import {
  useCreateLeadMutation,
  useGetCitiesQuery,
  useRecordTypeAppMutation,
  useResendLeadOtpMutation,
  useVerifyLeadOtpMutation
} from 'redux/api/leadsApi'
import { getUtmParamsSelector } from 'redux/store/slices/loanSlice'
import { getLeadIdSelector } from 'redux/store/slices/userSlice'
import { ApplicationEvents } from 'share/enums/events'
import type { Nullable, Undefinable } from 'share/types'
import { VerifyLeadOtpErrorType } from 'share/types/api/leads'
import tw from 'twin.macro'
import { ModeApp, pathnames, recaptchaSitekey } from 'utils/constants'
import Emitter from 'utils/emitter'
import { IINMask, PhoneMask } from 'utils/i-masks'
import notificationsWs from 'utils/sockets/notifications-ws'
import { validateAppType } from 'utils/validate-apptype'
import { validateIIN } from 'utils/validateIIN'
import * as yup from 'yup'

import OtpVerification from '../OtpVerification'
import LeadConditions from './LeadConditions'

type FormSchemaValues = yup.InferType<typeof formSchema>

type OtpSchemaValues = yup.InferType<typeof otpSchema>

const formSchema = yup
  .object({
    iin: yup
      .string()
      .min(12, 'create-lead-schema.iin-min')
      .max(12)
      .test('checkIin', 'create-lead-schema.iin-validation', value => validateIIN(value))
      .required(),
    phone: yup.string().min(11, 'create-lead-schema.phone-min').max(11).required(),
    city: yup
      .object()
      .shape({
        id: yup.string().required(),
        name: yup.string().required(),
        has_delivery: yup.boolean().notRequired()
      })
      .required(),
    bank_manger_code: yup.string().max(4).notRequired(),
    personal_data_agreement: yup.bool().oneOf([true], 'Field must be checked').required(),
    not_foreigner: yup.bool().oneOf([true], 'Field must be checked').required(),
    not_covered_by_fatca: yup.bool().oneOf([true], 'Field must be checked').required(),
    open_freedom_card: yup.bool().oneOf([true], 'Field must be checked').required(),
    agreement_to_get_documents: yup.bool().oneOf([true], 'Field must be checked').required(),
    agreement_to_independent_valuation: yup.bool().oneOf([true], 'Field must be checked').required(),
    beneficial_owner: yup.bool().oneOf([true], 'Field must be checked').required()
  })
  .required('create-lead-schema.required')

const otpSchema = yup.object({
  code: yup.string().min(6, 'create-lead-schema.otp-min').required('create-lead-schema.required')
})

const getTariffsDocument = (language: string) => {
  if (language === 'ru') return 'https://business.bankffin.kz/api/media/static_documents/STATIC_DOCUMENT/tariffs-ru.pdf'
  if (language === 'kk') return 'https://business.bankffin.kz/api/media/static_documents/STATIC_DOCUMENT/tariffs-kk.pdf'
  return undefined
}

const isProduction = ModeApp === 'production'

const PersonalInfo = () => {
  // Tools
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    t,
    i18n: { language }
  } = useTranslation()

  // Selectors
  const leadId = useAppSelector(getLeadIdSelector)
  const utmParams = useAppSelector(getUtmParamsSelector)

  // State
  const [reCaptchaToken, setReCaptchaToken] = useState<Nullable<string>>(null)
  const [openMoreInfo, setOpenMoreInfo] = useState<boolean>(false)
  const [openBeneficialInfo, setOpenBeneficialInfo] = useState<boolean>(false)
  const [isOtpSended, setIsOtpSended] = useState<boolean>(false)
  const [otpErrorMsg, setOtpErrorMsg] = useState<Undefinable<string>>(undefined)
  const dataFromUser: string =
    sessionStorage.getItem('dataFromUser') && JSON.parse(sessionStorage.getItem('dataFromUser') || '')

  // Query
  const { data: cities, isLoading: isCitiesLoading } = useGetCitiesQuery()

  // Mutations
  const [createLead, { isLoading: isLeadCreating, isError, error }] = useCreateLeadMutation()
  const [resendOtp] = useResendLeadOtpMutation()
  const [verifyOtp, { isLoading: isOtpVerifying }] = useVerifyLeadOtpMutation()
  const [recordTypeApp] = useRecordTypeAppMutation()
  const [getInfoUser, { data: userData }] = useGetDecryptedUserInfoMutation()
  // Forms
  const {
    control,
    formState: { isValid },
    getValues,
    handleSubmit,
    setError,
    setValue
  } = useForm<FormSchemaValues>({
    defaultValues: {
      phone: '',
      iin: '',
      personal_data_agreement: true,
      not_foreigner: true,
      not_covered_by_fatca: true,
      open_freedom_card: true,
      agreement_to_get_documents: true,
      agreement_to_independent_valuation: true,
      beneficial_owner: true
    },
    mode: 'onTouched',
    resolver: yupResolver(formSchema)
  })

  const { control: otpControl } = useForm<OtpSchemaValues>({
    defaultValues: {
      code: ''
    },
    mode: 'onChange',
    resolver: yupResolver(otpSchema)
  })

  const isContinueBtnDisabled = isProduction ? !reCaptchaToken || !isValid : !isValid

  // Handlers
  const handleReCaptchaChange = (token: string | null) => {
    setReCaptchaToken(token)
  }

  const handleChangePhone = () => {
    setIsOtpSended(false)
    setReCaptchaToken(null)
  }

  const handleOtpVerify = async (value: string | undefined) => {
    try {
      const { credit_application_id } = await verifyOtp({
        uuid: leadId as string,
        passcode: value as string,
        utm_params: utmParams
      }).unwrap()
      const dataForRecordTypeApp = {
        step_uuid: credit_application_id as string,
        type: searchParams.get('appType') || 'web'
      }
      recordTypeApp(dataForRecordTypeApp).unwrap()
      setOtpErrorMsg(undefined)
      dispatch(notificationsWs({ iin: getValues().iin }))
      setSearchParams(prev => {
        prev.delete('appType')
        prev.delete('data')
        return prev
      })
      Emitter.emit(ApplicationEvents.NAVIGATE_TO_NEXT_PAGE)
    } catch (err) {
      const {
        data: { detail }
      } = err as VerifyLeadOtpErrorType
      setOtpErrorMsg(detail)
    }
  }

  const handleOtpResend = useCallback(async () => {
    try {
      await resendOtp(leadId as string)
    } catch (error) {
      setOtpErrorMsg('Не получилось переотправить смс')
      console.log(error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId])

  const onSubmit = async (data: FormSchemaValues) => {
    try {
      if (isProduction && !reCaptchaToken) return
      let dataForRequest = {
        iin: data.iin,
        mobile_phone: `+${data.phone}`,
        program: searchParams.get('program') as string,
        city: data.city.id,
        recaptcha: isProduction ? reCaptchaToken : undefined,
        credit_params: {
          gesv: Number(searchParams.get('gesv')),
          interest_rate: Number(searchParams.get('interest_rate')),
          loan_amount: Number(searchParams.get('amount')),
          monthly_payment: Number(searchParams.get('monthly_payment')),
          period: Number(searchParams.get('period')),
          repayment_method: searchParams.get('repayment_method') as string
        }
      }
      if (dataFromUser && Object.keys(dataFromUser)) {
        dataForRequest = {
          ...dataForRequest,
          ...{
            lead_generator: {
              code: dataFromUser?.code,
              lead_platform: dataFromUser?.lead_platform,
              external_id: dataFromUser?.external_id
            }
          }
        }
      }
      await createLead(dataForRequest).unwrap()
      sessionStorage.removeItem('dataFromUser')
      setIsOtpSended(true)
    } catch (error) {
      console.log(error?.data.mobile_phone[0])
      setError('phone', { type: 'custom', message: `${error?.data.mobile_phone[0]}` })
      setError('iin', { type: 'custom', message: `${error?.data.iin[0]}` })
    }
  }

  // Effects
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search)
    const appType = qs.get('appType')
    const encryptedData = qs.get('data')

    const checkAppTypeAndData = async () => {
      try {
        const type = await validateAppType(appType)
        if (!type || !encryptedData) return
        const res = await getInfoUser({ data: encryptedData }).unwrap()
        setValue('phone', res.mobile_phone.replace('+', ''))
        setValue('iin', res.iin)
      } catch (error) {
        return
      }
    }

    checkAppTypeAndData()
  }, [getInfoUser, setValue])

  // Check query params, if one of these params does not have, then will be redirect to calc page
  useEffect(() => {
    const interest_rate = searchParams.get('interest_rate')
    const period = searchParams.get('period')
    const amount = searchParams.get('amount')
    const program = searchParams.get('program')
    const gesv = searchParams.get('gesv')
    const monthlyPayment = searchParams.get('monthly_payment')
    const repaymentMethod = searchParams.get('repayment_method')
    if (!interest_rate || !period || !amount || !program || !gesv || !monthlyPayment || !repaymentMethod) {
      navigate(pathnames.index)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  return (
    <ContainerWithSteps>
      {!isOtpSended ? (
        <form tw='space-y-3' onSubmit={handleSubmit(onSubmit)}>
          <InfoBlock twStyle={tw`mt-[10px] md:mt-0`}>
            <Typography variant='body' weight='bold'>
              {t('flow-your-info')}
            </Typography>
            <div tw='grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-[12px] mt-4'>
              <MaskedInput
                t={t}
                label={t('flow-mobile-number')}
                placeholder='+7 (---) --- -- --'
                name='phone'
                control={control}
                disabled={!!userData?.mobile_phone.length}
                maskOptions={{
                  mask: PhoneMask
                }}
              />
              <MaskedInput
                t={t}
                label={t('flow-iin')}
                name='iin'
                control={control}
                disabled={!!userData?.iin.length}
                placeholder={t('flow-iin-placeholder') as string}
                maskOptions={IINMask}
              />
            </div>
            <Select
              control={control}
              name='city'
              placeholder={t('cities-ph')}
              label={t('flow-enter-city')}
              twStyle={tw`mt-4`}
              isSearchable
              isDisabled={isCitiesLoading}
              options={cities}
              noOptionsMessage={() => t('flow-not-founded')}
              getOptionLabel={option => (language === 'ru' ? option.name : option.name_kk)}
              getOptionValue={option => option.id}
            />
            {isError && error?.data.detail && (
              <Typography variant='subbody' twStyle={tw`mt-4 text-red-light px-3`}>
                {error?.data.detail[0]}
              </Typography>
            )}
          </InfoBlock>
          <InfoBlock>
            <Typography variant='body' weight='bold'>
              {t('flow-agreement')}
            </Typography>
            <div tw='flex flex-col'>
              <Checkbox control={control} name='personal_data_agreement'>
                <Typography variant='subbody' text>
                  {t('flow-agreement-personal-data')}{' '}
                  <Link href={AgreementFile} target='_blank' rel='noreferrer'>
                    {t('flow-agreement-personal-data2')}{' '}
                  </Link>
                  {t('flow-agreement-personal-data3')}
                </Typography>
              </Checkbox>
              <Checkbox control={control} name='not_foreigner'>
                <Typography variant='subbody' text>
                  {t('flow-agreement-not-public')}
                </Typography>
              </Checkbox>
              <Checkbox control={control} name='beneficial_owner'>
                <Typography variant='subbody' text>
                  {t('flow-agreement-not-benef')}
                </Typography>
                <ChevronUpIcon
                  css={[
                    tw`inline-block cursor-pointer transition-transform pointer-events-auto`,
                    openBeneficialInfo && tw`-rotate-180`
                  ]}
                  onClick={(e: MouseEvent) => {
                    e.preventDefault()
                    setOpenBeneficialInfo(prev => !prev)
                  }}
                />
              </Checkbox>
              <LeadConditions isOpened={openBeneficialInfo} variant={'2'} />
              <Checkbox control={control} name='not_covered_by_fatca'>
                <Typography variant='subbody' text>
                  {t('flow-agreement-usa-bills')}
                  <ChevronUpIcon
                    css={[
                      tw`inline-block cursor-pointer transition-transform pointer-events-auto`,
                      openMoreInfo && tw`-rotate-180`
                    ]}
                    onClick={(e: MouseEvent) => {
                      e.preventDefault()
                      setOpenMoreInfo(prev => !prev)
                    }}
                  />
                </Typography>
              </Checkbox>
              <LeadConditions isOpened={openMoreInfo} variant={'1'} />
              <Checkbox control={control} name='open_freedom_card'>
                <div>
                  <Typography variant='subbody' text>
                    <Trans
                      components={{
                        a: <Link href={IPServiceAgreement} target='_blank' rel='noreferrer' />
                      }}
                    >
                      {t('flow-agreement-open')}
                    </Trans>
                  </Typography>
                  <ul tw='list-disc list-inside  marker:text-primary-middle'>
                    <li>
                      <Typography variant='subbody' as='span' twStyle={tw`-m-2`}>
                        <Trans
                          components={{
                            a: <Link href={getTariffsDocument(language)} target='_blank' rel='noreferrer' />
                          }}
                        >
                          {t('flow-agreement-link-1')}
                        </Trans>
                      </Typography>
                    </li>
                    <li>
                      <Typography variant='subbody' as='span' twStyle={tw`-m-2`}>
                        <Trans
                          components={{
                            a: <Link href={TermsOfQuarantee} target='_blank' rel='noreferrer' />
                          }}
                        >
                          {t('flow-agreement-link-2')}
                        </Trans>
                      </Typography>
                    </li>
                  </ul>
                </div>
              </Checkbox>
              <Checkbox control={control} name='agreement_to_independent_valuation'>
                <Typography variant='subbody' text>
                  {t('flow-agreement-estimate')}
                </Typography>
              </Checkbox>
              {isProduction && (
                <ReCAPTCHA hl={language} tw='mt-4' sitekey={recaptchaSitekey} onChange={handleReCaptchaChange} />
              )}
            </div>
          </InfoBlock>
          <InfoBlock>
            <div className='flex items-start gap-3'>
              <div className='shrink-0'>
                <InfoIcon />
              </div>
              <BodyText
                text={t('flow-personal-info-warning')}
                className='text-[14px] leading-[19.6px] text-[var(--text-landing-subtitle)]'
              />
            </div>
          </InfoBlock>
          <div tw='flex justify-end px-4 md:px-0 !mt-8 md:!mt-4'>
            <Button variant='primary' loading={isLeadCreating} disabled={isContinueBtnDisabled}>
              <Typography variant='body' weight='bold' twStyle={tw`text-white`}>
                {t('flow-continue')}
              </Typography>
              <Typography variant='caption' twStyle={tw`text-white`}>
                {t('flow-get-sms')}
              </Typography>
            </Button>
          </div>
        </form>
      ) : (
        <Fragment>
          <OtpVerification
            variant='phone-otp'
            otpLength={6}
            control={otpControl}
            errorMsg={otpErrorMsg}
            name='code'
            disabled={isOtpVerifying}
            onChangePhone={handleChangePhone}
            onOtpVerify={handleOtpVerify}
            onOtpTimerReset={handleOtpResend}
          />
        </Fragment>
      )}
    </ContainerWithSteps>
  )
}

export default PersonalInfo
